import * as React from "react";
import { CheckCircle } from "react-feather";
import { Skeleton } from "antd";
import { HEALTH_COLORS } from "../utils";

type CardVariant = "workload" | "domain" | "quota" | "agent";

interface HealthSummaryCardProps {
  title: string;
  icon: React.ReactNode;
  variant: CardVariant;
  data: {
    ready?: number;
    warning?: number;
    error?: number;
    active?: number;
    recentlyInactive?: number;
    neverActive?: number;
    checking?: number;
    ok?: number;
    nearLimit?: number;
    exceeded?: number;
  };
  onClick?: () => void;
  isLoading?: boolean;
}

interface StatRowProps {
  count: number;
  label: string;
  color: string;
}

const StatRow: React.FC<StatRowProps> = ({ count, label, color }) => (
  <div className="flex items-center gap-2">
    <span
      className="w-2 h-2 rounded-full"
      style={{ backgroundColor: color }}
    />
    <span className="text-sm" style={{ color: "var(--color-default)" }}>
      {count} {label}
    </span>
  </div>
);

export const HealthSummaryCard: React.FC<HealthSummaryCardProps> = ({ title, icon, variant, data, onClick, isLoading }) => {
  const {
    ready = 0,
    warning = 0,
    error = 0,
    active = 0,
    recentlyInactive = 0,
    neverActive = 0,
    checking = 0,
    ok = 0,
    nearLimit = 0,
    exceeded = 0,
  } = data;

  // Loading skeleton state
  if (isLoading) {
    return (
      <div
        className="flex-1 rounded-lg p-4"
        style={{
          backgroundColor: "var(--health-card-bg)",
          border: "1px solid var(--color-border)",
          minWidth: 0,
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div style={{ color: "var(--color-label)" }}>{icon}</div>
          <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
            {title}
          </span>
        </div>
        {/* Skeleton content */}
        <div className="flex flex-col items-center py-2">
          <Skeleton.Avatar active size={32} shape="circle" />
          <Skeleton.Input active size="small" style={{ width: 80, marginTop: 8 }} />
        </div>
      </div>
    );
  }

  // Get labels and colors based on variant
  const getConfig = () => {
    switch (variant) {
      case "agent":
        return {
          labels: { good: "Active", warn: "Recently Inactive", bad: "Never Active" },
          warnColor: HEALTH_COLORS.error,
          badColor: HEALTH_COLORS.inactive,
        };
      case "quota":
        return {
          labels: { good: "OK", warn: "Near limit", bad: "At limit" },
          warnColor: HEALTH_COLORS.warning,
          badColor: HEALTH_COLORS.error,
        };
      default: // workload, domain
        return {
          labels: { good: "Ready", warn: "Warning", bad: "Error" },
          warnColor: HEALTH_COLORS.warning,
          badColor: HEALTH_COLORS.error,
        };
    }
  };

  const { labels, warnColor, badColor } = getConfig();

  // Get counts based on variant
  const goodCount = variant === "agent" ? active : variant === "quota" ? ok : ready;
  const warnCount = variant === "agent" ? recentlyInactive : variant === "quota" ? nearLimit : warning;
  const badCount = variant === "agent" ? neverActive : variant === "quota" ? exceeded : error;

  // Check if all items are healthy (no bad or warning states)
  // For agents, also check that there are no agents in "checking" state
  const isAllHealthy = badCount === 0 && warnCount === 0 && (variant !== "agent" || checking === 0);

  // Build stats array in priority order, filtered to non-zero counts
  // For agent variant: Recently Inactive > Checking > Never Active > Active
  // For others: Bad/Error > Warning > Good/Ready
  const stats =
    variant === "agent"
      ? [
          { count: warnCount, label: labels.warn, color: warnColor }, // Recently Inactive
          { count: checking, label: "Checking", color: HEALTH_COLORS.inactive }, // Checking
          { count: badCount, label: labels.bad, color: badColor }, // Never Active
          { count: goodCount, label: labels.good, color: HEALTH_COLORS.ready }, // Active
        ].filter((s) => s.count > 0)
      : [
          { count: badCount, label: labels.bad, color: badColor },
          { count: warnCount, label: labels.warn, color: warnColor },
          { count: goodCount, label: labels.good, color: HEALTH_COLORS.ready },
        ].filter((s) => s.count > 0);

  // The prominent stat is the first one (highest priority non-zero)
  const prominentStat = stats[0];
  // Remaining stats for the vertical list
  const remainingStats = stats.slice(1);

  return (
    <div
      onClick={onClick}
      className={`flex-1 rounded-lg p-4 transition-all ${onClick ? "cursor-pointer hover:shadow-md" : ""}`}
      style={{
        backgroundColor: "var(--health-card-bg)",
        border: "1px solid var(--color-border)",
        minWidth: 0,
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div style={{ color: "var(--color-label)" }}>{icon}</div>
        <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
          {title}
        </span>
      </div>

      {/* Content */}
      {isAllHealthy ? (
        /* All Healthy State */
        <div className="flex flex-col items-center justify-center py-2">
          <CheckCircle size={32} color={HEALTH_COLORS.ready} />
          <span className="text-sm font-medium mt-2" style={{ color: HEALTH_COLORS.ready }}>
            All {labels.good}
          </span>
        </div>
      ) : (
        /* Issues Exist State */
        <div className="flex flex-col items-center">
          {/* Prominent Value */}
          {prominentStat && (
            <div className="flex flex-col items-center mb-3">
              <span
                className="text-3xl font-bold"
                style={{ color: prominentStat.color }}
              >
                {prominentStat.count}
              </span>
              <span
                className="text-xs px-2 py-0.5 rounded-full mt-1"
                style={{
                  backgroundColor: `${prominentStat.color}20`,
                  color: prominentStat.color,
                }}
              >
                {prominentStat.label}
              </span>
            </div>
          )}

          {/* Remaining Stats List */}
          {remainingStats.length > 0 && (
            <div className="flex flex-col gap-1 mt-1">
              {remainingStats.map((stat) => (
                <StatRow
                  key={stat.label}
                  count={stat.count}
                  label={stat.label}
                  color={stat.color}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
